import { useEffect, useRef, useState } from "react";
import { flascheDarstellen } from "../dienste/flasche3d.js";
import { fotoUrlHolen } from "../dienste/fotos.js";
import { kapselArt } from "../kern/flasche.js";
import { t } from "../i18n/index.js";


/* ═══════════════════════════════════════════════════════════════
   Flasche als drehbares Modell

   Bedienung wie bei einem Gegenstand in der Hand: waagerecht ziehen
   dreht die Flasche, beim Loslassen läuft sie mit Schwung aus. In der
   großen Ansicht neigt senkrechtes Ziehen sie zusätzlich. In der
   kleinen Ansicht bleibt senkrechtes Wischen dem Seitenscrollen
   vorbehalten (`touch-action: pan-y`), sonst bliebe man auf dem
   Telefon an der Flasche hängen.

   Vergrößern nur in der großen Ansicht: zwei Finger auseinander
   (dabei verschieben sie das Bild auch), Mausrad oder Trackpad,
   Doppeltippen (hin und zurück), Tasten + − 0. Vergrößert zieht
   senkrechtes Ziehen das Bild hoch und runter statt die Flasche zu
   neigen — man will am Etikett entlang, nicht kippen.

   Gezeichnet wird nur, wenn sich etwas bewegt. Das langsame Drehen
   im Ruhezustand läuft nur, solange die Flasche sichtbar ist, endet
   beim ersten Anfassen und entfällt bei „Bewegung reduzieren“.

   Die Haut wird in zwei Stufen geladen: erst die kleine Fassung (sie
   liegt für den Offline-Betrieb im Zwischenspeicher), dann die
   große. Fehlt die große ohne Netz, bleibt es bei der kleinen.
   ═══════════════════════════════════════════════════════════════ */

function ladeBild(url) {
  return new Promise((ok, weg) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => ok(img);
    img.onerror = () => weg(new Error("Bild nicht ladbar"));
    img.src = url;
  });
}

const ZOOM_MAX = 4;

const reduziert = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; }
};


/* `vorne` (0..1): Stelle der Abwicklung, die beim Öffnen nach vorn
   zeigt — das Hauptetikett. Die Mitte der Abwicklung (0,5) zeigt ohne
   Drehung zum Betrachter; jede andere Stelle braucht die Drehung
   2π · (0,5 − vorne). */
function FlaschenModell({ form, kapsel, pfade = null, bild = null, vorne = 0.5, gross = false, selbstDrehen = !gross, onTippen = null, alt = "" }) {
  const leinwandRef = useRef(null);
  const zeichnerRef = useRef(null);
  const startWinkel = Number.isFinite(vorne) ? 2 * Math.PI * (0.5 - vorne) : 0;
  const lage = useRef({
    winkel: startWinkel, neigung: 0.12, tempo: 0, dreht: false, zug: null, raf: 0, letzt: 0, sichtbar: true,
    zoom: 1, vx: 0, vy: 0, finger: new Map(), zange: null, tipp: null,
  });
  const [ohneWebgl, setOhneWebgl] = useState(false);
  const [flachUrl, setFlachUrl] = useState(null);

  const schleife = (zeit) => {
    const l = lage.current;
    l.raf = 0;
    const z = zeichnerRef.current;
    if (!z) return;
    const dt = l.letzt ? Math.min(0.05, (zeit - l.letzt) / 1000) : 0;
    l.letzt = zeit;
    if (l.dreht && l.sichtbar && !l.zug) l.winkel += dt * 0.32;
    if (l.tempo && !l.zug) {
      l.winkel += l.tempo * dt;
      l.tempo *= Math.exp(-dt * 3.2);
      if (Math.abs(l.tempo) < 0.03) l.tempo = 0;
    }
    z.zeichne({ winkel: l.winkel, neigung: l.neigung, zoom: l.zoom, versatz: [l.vx, l.vy] });
    if ((l.dreht && l.sichtbar) || l.tempo) l.raf = requestAnimationFrame(schleife);
    else l.letzt = 0;
  };
  const anstossen = () => {
    const l = lage.current;
    if (!l.raf && zeichnerRef.current) l.raf = requestAnimationFrame(schleife);
  };

  /* Zeichner anlegen und wieder freigeben. */
  useEffect(() => {
    const leinwand = leinwandRef.current;
    let z = null;
    try { z = flascheDarstellen(leinwand, { form, kapsel, beiVerlust: () => setOhneWebgl(true) }); }
    catch (e) { z = null; }
    if (!z) { setOhneWebgl(true); return undefined; }
    zeichnerRef.current = z;
    lage.current.dreht = selbstDrehen && !reduziert();
    anstossen();

    const beob = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => anstossen()) : null;
    if (beob) beob.observe(leinwand);
    const sicht = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver((e) => { lage.current.sichtbar = e.some((x) => x.isIntersecting); anstossen(); })
      : null;
    if (sicht) sicht.observe(leinwand);
    return () => {
      if (beob) beob.disconnect();
      if (sicht) sicht.disconnect();
      if (lage.current.raf) cancelAnimationFrame(lage.current.raf);
      lage.current.raf = 0;
      z.entsorgen();
      zeichnerRef.current = null;
    };
  }, []);

  const farbe = kapsel && kapsel.farbe ? kapsel.farbe.join(",") : "";
  useEffect(() => {
    if (!zeichnerRef.current) return;
    zeichnerRef.current.setzeModell({ form, kapsel });
    anstossen();
  }, [form, kapsel && kapsel.art, kapsel && kapsel.anteil, farbe]);

  /* Haut: direkt übergebenes Bild (Vorschau in der Aufnahme) oder
     gespeicherte Pfade, kleinste zuerst. */
  const pfadSchluessel = (pfade || []).join("|");
  useEffect(() => {
    let ab = false;
    if (bild) {
      if (zeichnerRef.current) { zeichnerRef.current.setzeHaut(bild); anstossen(); }
      return undefined;
    }
    (async () => {
      for (const pfad of (pfade || []).filter(Boolean)) {
        try {
          const url = await fotoUrlHolen(pfad);
          const img = await ladeBild(url);
          if (ab) return;
          setFlachUrl(url);
          if (zeichnerRef.current) { zeichnerRef.current.setzeHaut(img); anstossen(); }
        } catch (e) { /* nächste Stufe versuchen; ohne Netz bleibt die kleine */ }
      }
    })();
    return () => { ab = true; };
  }, [bild, pfadSchluessel]);

  /* ── Vergrößern ── */
  /* Bildschirmpunkt → Bildkoordinaten der Leinwand (−1 … 1, y oben) */
  const bildPunkt = (x, y) => {
    const r = leinwandRef.current.getBoundingClientRect();
    return [((x - r.left) / Math.max(1, r.width)) * 2 - 1, 1 - ((y - r.top) / Math.max(1, r.height)) * 2];
  };
  /* Rand: Das vergrößerte Bild deckt die Leinwand immer ganz */
  const begrenzen = () => {
    const l = lage.current, m = l.zoom - 1;
    l.vx = Math.max(-m, Math.min(m, l.vx));
    l.vy = Math.max(-m, Math.min(m, l.vy));
  };
  /* Auf `z` vergrößern; der Punkt (px, py) bleibt, wo er ist */
  const zoomAuf = (z, px = 0, py = 0) => {
    const l = lage.current;
    const neu = Math.max(1, Math.min(ZOOM_MAX, z));
    l.vx = px - (neu / l.zoom) * (px - l.vx);
    l.vy = py - (neu / l.zoom) * (py - l.vy);
    l.zoom = neu;
    begrenzen();
    anstossen();
  };
  const verschieben = (dx, dy) => {
    const l = lage.current, el = leinwandRef.current;
    l.vx += (2 * dx) / Math.max(1, el.clientWidth);
    l.vy -= (2 * dy) / Math.max(1, el.clientHeight);
    begrenzen();
  };

  /* Mausrad und Trackpad (auch Zusammenziehen auf dem Trackpad, das
     als Rad mit Strg kommt). Nicht passiv, sonst rollt die Seite. */
  useEffect(() => {
    const el = leinwandRef.current;
    if (!gross || !el) return undefined;
    const rad = (e) => {
      e.preventDefault();
      const l = lage.current;
      l.dreht = false;
      const [px, py] = bildPunkt(e.clientX, e.clientY);
      const schritt = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomAuf(l.zoom * Math.exp(-schritt * (e.ctrlKey ? 0.01 : 0.0015)), px, py);
    };
    el.addEventListener("wheel", rad, { passive: false });
    return () => el.removeEventListener("wheel", rad);
  }, [gross, ohneWebgl]);

  /* ── Ziehen, Schwung, Tippen, zwei Finger ── */
  const zangeBeginnen = () => {
    const l = lage.current;
    const [a, b] = [...l.finger.values()];
    const mitte = bildPunkt((a.x + b.x) / 2, (a.y + b.y) / 2);
    l.zange = { abstand: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mitte, zoom: l.zoom, vx: l.vx, vy: l.vy };
    l.zug = null;
    l.tempo = 0;
  };
  const runter = (e) => {
    const l = lage.current;
    l.dreht = false;
    l.tempo = 0;
    l.finger.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ältere Browser */ }
    if (gross && l.finger.size === 2) { zangeBeginnen(); return; }
    if (l.finger.size > 1) return;
    l.zug = { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, t0: performance.now(), t: performance.now(), v: 0, weit: false };
  };
  const bewegen = (e) => {
    const l = lage.current;
    if (l.finger.has(e.pointerId)) l.finger.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (l.zange && l.finger.size >= 2) {
      /* Der Punkt unter der Fingermitte vom Anfang folgt der Fingermitte */
      const [a, b] = [...l.finger.values()];
      const z0 = l.zange;
      const z = Math.max(1, Math.min(ZOOM_MAX, z0.zoom * Math.hypot(a.x - b.x, a.y - b.y) / z0.abstand));
      const [px, py] = bildPunkt((a.x + b.x) / 2, (a.y + b.y) / 2);
      l.vx = px - z * (z0.mitte[0] - z0.vx) / z0.zoom;
      l.vy = py - z * (z0.mitte[1] - z0.vy) / z0.zoom;
      l.zoom = z;
      begrenzen();
      anstossen();
      return;
    }
    if (!l.zug) return;
    const breite = (leinwandRef.current && leinwandRef.current.clientWidth) || 200;
    const faktor = (Math.PI * 2) / Math.max(240, breite * 1.6) / l.zoom;
    const dx = e.clientX - l.zug.x, dy = e.clientY - l.zug.y;
    const jetzt = performance.now();
    l.winkel += dx * faktor;
    if (gross && l.zoom > 1.02) verschieben(0, dy);
    else if (gross) l.neigung = Math.max(-0.35, Math.min(0.6, l.neigung + dy * faktor * 0.6));
    const dt = Math.max(1, jetzt - l.zug.t);
    l.zug.v = 0.7 * l.zug.v + 0.3 * (dx * faktor * 1000 / dt);
    l.zug.x = e.clientX; l.zug.y = e.clientY; l.zug.t = jetzt;
    if (Math.hypot(e.clientX - l.zug.x0, e.clientY - l.zug.y0) > 6) l.zug.weit = true;
    anstossen();
  };
  const hoch = (e) => {
    const l = lage.current;
    l.finger.delete(e.pointerId);
    if (l.zange) {
      /* Nach zwei Fingern weder Schwung noch Tippen; ein verbliebener
         Finger zieht von seiner jetzigen Stelle weiter */
      if (l.finger.size < 2) l.zange = null;
      if (l.finger.size === 1) {
        const [f] = [...l.finger.values()];
        l.zug = { x: f.x, y: f.y, x0: f.x, y0: f.y, t0: 0, t: performance.now(), v: 0, weit: true };
      }
      return;
    }
    if (!l.zug) return;
    const tipp = !l.zug.weit && performance.now() - l.zug.t0 < 450;
    l.tempo = tipp ? 0 : Math.max(-9, Math.min(9, l.zug.v));
    l.zug = null;
    anstossen();
    if (!tipp) return;
    if (gross) {
      /* Doppeltippen: vergrößern um die Stelle, vergrößert zurück */
      const jetzt = performance.now(), vorher = l.tipp;
      l.tipp = { t: jetzt, x: e.clientX, y: e.clientY };
      if (vorher && jetzt - vorher.t < 350 && Math.hypot(e.clientX - vorher.x, e.clientY - vorher.y) < 30) {
        l.tipp = null;
        const [px, py] = bildPunkt(e.clientX, e.clientY);
        zoomAuf(l.zoom > 1.02 ? 1 : 2.5, px, py);
      }
    } else if (onTippen) onTippen();
  };
  const taste = (e) => {
    const l = lage.current;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      l.dreht = false;
      l.winkel += (e.key === "ArrowLeft" ? -1 : 1) * Math.PI / 12;
      anstossen();
    } else if (gross && (e.key === "+" || e.key === "=" || e.key === "-" || e.key === "0")) {
      e.preventDefault();
      l.dreht = false;
      zoomAuf(e.key === "0" ? 1 : l.zoom * (e.key === "-" ? 1 / 1.4 : 1.4), 0, 0);
    } else if (gross && l.zoom > 1.02 && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      verschieben(0, (e.key === "ArrowUp" ? 1 : -1) * 0.1 * leinwandRef.current.clientHeight);
      anstossen();
    } else if ((e.key === "Enter" || e.key === " ") && onTippen) {
      e.preventDefault();
      onTippen();
    }
  };

  if (ohneWebgl) {
    /* Ohne WebGL: die Abwicklung flach, seitlich verschiebbar. */
    return (
      <div className={"wl-rundumflach" + (gross ? " ist-gross" : "")}>
        {flachUrl || bild
          ? <img src={flachUrl || (bild && bild.toDataURL ? bild.toDataURL("image/jpeg", 0.8) : "")} alt={alt} />
          : <span className="wl-nebenzeile">{t("Lädt …")}</span>}
      </div>
    );
  }
  return (
    <canvas ref={leinwandRef}
      className={"wl-flaschenmodell" + (gross ? " ist-gross" : "")}
      role="img" aria-label={alt} tabIndex={0}
      title={t("Zum Drehen ziehen")}
      onPointerDown={runter} onPointerMove={bewegen} onPointerUp={hoch} onPointerCancel={hoch}
      onKeyDown={taste} />
  );
}


/** Große Ansicht im Vollbild: drehen, neigen, vergrößern. Schließt
    mit ×, Escape oder Tippen neben die Flasche. Escape geht nicht an
    umgebende Dialoge weiter (in der Aufnahme bräche es sonst alles ab).
    Grund: hell und deckend wie die Seite, ohne Rahmen. */
function FlaschenLupe({ onSchliessen, alt = "", ...modell }) {
  return (
    <div className="wl-rundumlupe" role="dialog" aria-modal="true" aria-label={alt}
      onClick={(e) => { if (e.target === e.currentTarget) onSchliessen(); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onSchliessen(); } }}>
      <button className="wl-rundumzu" onClick={onSchliessen} aria-label={t("Schließen")} autoFocus>×</button>
      <FlaschenModell {...modell} alt={alt} gross />
      <span className="wl-rundumtipp">{t("Ziehen dreht die Flasche · zwei Finger, Mausrad oder Doppeltippen vergrößern")}</span>
    </div>
  );
}


/** Rundum-Etikett eines Jahrgangs als kleine drehbare Flasche;
    Antippen öffnet die große Ansicht. Die Kapselart folgt der
    Aufnahme; ältere Einträge ohne Angabe richten sich nach den Feldern
    „Kapsel“/„Verschluss“. Größe über die Klasse des Rahmens. */
function RundumVorschau({ jg, alt = "", className = "wl-rundumklein", children = null }) {
  const [gross, setGross] = useState(false);
  const r = jg && jg.rundum;
  if (!r) return null;
  const kapsel = { art: (r.kapsel && r.kapsel.art) || kapselArt(jg), anteil: r.kapsel ? r.kapsel.anteil : undefined, farbe: r.kapsel ? r.kapsel.farbe : undefined };
  const pfade = [r.thumb, r.pfad].filter((p, i, alle) => p && alle.indexOf(p) === i);
  const form = r.profil || r.form;
  return (
    <>
      <div className={className}>
        <FlaschenModell form={form} kapsel={kapsel} pfade={pfade} vorne={r.vorne} alt={alt} onTippen={() => setGross(true)} />
        {children}
      </div>
      {gross && <FlaschenLupe form={form} kapsel={kapsel} pfade={pfade} vorne={r.vorne} alt={alt} onSchliessen={() => setGross(false)} />}
    </>
  );
}


export { FlaschenLupe, FlaschenModell, RundumVorschau };
